import { flash } from "@xola/ui-kit";
import { requiredString } from "common";
import { omit } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import {
    Affiliate,
    AffiliateDepositAmountType,
    AffiliateDepositScope,
    AffiliateDepositType,
    Delegate,
    DelegatePermission,
    DelegateRole,
    Permission,
} from "types";
import { boolean, number, object } from "yup";
import { api } from "../../api/api";
import { isDecimalNumber } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { IsGranted } from "../Common/IsGranted";
import { AgentPurchaseSettingsForm, AgentPurchaseSettingsFormValues } from "./AgentPurchaseSettingsForm";

interface AgentPurchaseSettingsProps {
    agent: Affiliate;
    onSave: (agentId: ID) => void;
}

export const AgentPurchaseSettings = ({ agent, onSave }: AgentPurchaseSettingsProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const isDelegated = agent.delegated && !!agent.user?.id;
    const delegateData = seller.delegates?.find((delegate) => delegate.id === agent.user?.id);
    const initialValues = getInitialValues(agent, delegateData);
    const existingAgent = !!agent?.id;

    const { mutateAsync: updateDelegate, isLoading: isUpdatingDelegate } = useMutation(async (payload: Delegate) => {
        return api.put(`/users/${seller.id}/delegates/${agent.user?.id}`, payload);
    });

    const { mutateAsync: updateAgent, isLoading: isUpdatingAgent } = useMutation(async (payload: Affiliate) => {
        return api.put(`/affiliates/${agent.id}`, payload);
    });

    const handleFormSubmit = async (values: AgentPurchaseSettingsFormValues) => {
        const payload = getAffiliateUpdatePayload(values, agent);
        await updateAgent(payload);
        if (isDelegated && delegateData) {
            const delegatePayload = getDelegateUpdatePayload(values, delegateData);
            await updateDelegate(delegatePayload);
            invalidate("sellers", seller.id);
            invalidate("delegates", delegateData.id);
        }

        onSave(agent.id);
        flash.show({ text: `Agent successfully ${existingAgent ? "updated" : "created"}`, color: "success" });
    };

    const isLoading = isUpdatingDelegate || isUpdatingAgent;

    return (
        <IsGranted permissions={Permission.PARTNERS_PURCHASE_SETTINGS_VIEW}>
            <AgentPurchaseSettingsForm
                isLoading={isLoading}
                agent={agent}
                validationSchema={validationSchema}
                initialValues={initialValues}
                onFormSubmit={handleFormSubmit}
            />
        </IsGranted>
    );
};

const validationSchema = object().shape({
    affiliateNotification: object().shape({
        enabled: boolean().required(),
    }),
    travelerNotification: object().shape({
        enabled: boolean().required(),
    }),
    deposit: object().shape({
        amount: number()
            .required()
            .typeError("Invalid Type")
            .min(0, "should be zero or a positive number with up to two decimal places")
            .test("format", "should be zero or a positive number with up to two decimal places", (value) =>
                isDecimalNumber(value),
            )
            .when("amountType", {
                is: AffiliateDepositAmountType.PERCENT,
                then: number().max(100, "should be less than or equal to 100"),
            }),
        amountType: requiredString(),
        depositType: requiredString(),
        scope: requiredString(),
        enabled: boolean().required(),
        validate: boolean().required(),
    }),
    voucher: boolean().required(),
});

/**
 * Get Initial Values of form
 */
const getInitialValues = (agent: Affiliate, delegate?: Delegate): AgentPurchaseSettingsFormValues => {
    const affiliatePermission = delegate?.permissions?.find(
        (permission: DelegatePermission) => permission.name === DelegateRole.AFFILIATE,
    );
    return {
        selectedExperiences: affiliatePermission?.selectedExperiences ?? {
            all: true,
            experiences: [],
        },
        affiliateNotification: {
            enabled: agent.affiliateNotification?.enabled ?? false,
        },
        travelerNotification: {
            enabled: agent.travelerNotification?.enabled ?? true,
        },
        deposit: {
            amount: 0,
            enabled: true,
            validate: false,
            amountType: AffiliateDepositAmountType.ABSOLUTE,
            depositType: AffiliateDepositType.TOTAL,
            scope: AffiliateDepositScope.OUTING,
            ...(agent.deposit && { ...agent.deposit }),
        },
        voucher: agent.voucher ?? false,
    };
};

/**
 * Get Payload for Affiliate Update
 */
const getAffiliateUpdatePayload = (values: AgentPurchaseSettingsFormValues, agent: Affiliate) => {
    return { ...agent, ...omit(values, "selectedExperiences") };
};

const getDelegateUpdatePayload = (values: AgentPurchaseSettingsFormValues, delegateData: Delegate) => {
    const permissions: DelegatePermission[] = [...delegateData.permissions];
    const affiliatePermissionIndex = permissions.findIndex(
        (permission: DelegatePermission) => permission.name === DelegateRole.AFFILIATE,
    );
    permissions[affiliatePermissionIndex].selectedExperiences = values.selectedExperiences;
    return { ...delegateData, permissions };
};
